import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCombinedOrderItemsForPacking, useRoute, useRouteEdit } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import { Link } from 'react-router-dom';
import ROUTE_STATUS from '../../components/constants/routeStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import PackerDeliveryRow from './packerDeliveryRow';
import Helmet from 'react-helmet';
import classNames from 'classnames';
import PackerCombinedDeliveryOrderItemsTable from './packerCombinedDeliveryOrderItemsTable';
import OrderPackingStatusDisplay from '../../components/orderPackingStatus';

const PackerRouteView = ({ match, history }) => {
  const [combinedDeliveries, setCombinedDeliveries] = useState([]);
  const [fetchCombinedItems, setFetchCombinedItems] = useState(false);
  const [packedCombinedItemIds, setPackedCombinedItemIds] = useState([]);

  const {
    combinedOrderItems,
    isLoading: isCombinedOrderItemsLoading,
    isError: isCombinedOrderItemsError,
    error: combinedOrderItemsError,
  } = useCombinedOrderItemsForPacking({
    orderIds: combinedDeliveries
      .map((d) => (d.deliveryOrderBatch ? d.deliveryOrderBatch.orders.map((o) => o.id) : []))
      .flat(),
    enabled: fetchCombinedItems,
    excludePackedOrders: true,
    onlyFulfilledOrderItems: true,
  });

  useEffect(() => {
    // reset "Packed" item state on order batch change
    setPackedCombinedItemIds([]);
  }, [combinedOrderItems]);

  const {
    route,
    isLoading: loading,
    isError,
    error,
  } = useRoute({
    routeId: match.params.routeId,
  });

  const {
    editRoute,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useRouteEdit(
    (route) => {
      if (route.status === 'READY_FOR_PICKUP') {
        history.replace({
          pathname: routeLink,
        });
      }
    },
    () => window.scrollTo(0, 0)
  );

  const isReady = [ROUTE_STATUS.inPacking.name, ROUTE_STATUS.waitingForPickup.name].indexOf(route?.status) === -1;

  const routeLink = `/admin/packing/deliveryDays/${match.params.deliveryDayId}${
    match.params.customPackingType ? `/${match.params.customPackingType}` : ''
  }`;

  const statuses = Object.keys(ROUTE_STATUS).map((key) => {
    return ROUTE_STATUS[key];
  });
  const status = statuses.find((s) => s.name === route?.status);
  const { formatMessage } = useIntl();

  const groupedCombinedOrderItems = combinedOrderItems
    ? utils.groupByValueFunc(combinedOrderItems, (coi) => coi.orderItem.item.marketplaces[0].marketplaceName)
    : null;

  const packedGroupedCombinedOrderItemKeys = groupedCombinedOrderItems
    ? Object.keys(groupedCombinedOrderItems).filter((marketplaceName) =>
        groupedCombinedOrderItems[marketplaceName].every((gcoi) =>
          packedCombinedItemIds.some((id) => id === gcoi.orderItem.item.id)
        )
      )
    : null;

  const notPackedGroupedCombinedOrderItems = groupedCombinedOrderItems
    ? Object.keys(groupedCombinedOrderItems).filter(
        (marketplaceName) => !packedGroupedCombinedOrderItemKeys.some((key) => key === marketplaceName)
      )
    : null;

  const prePackedCombinedDeliveryOrders = combinedDeliveries?.reduce((result, delivery) => {
    return result.concat(delivery?.deliveryOrderBatch?.orders.filter((o) => o.packingStatus) || []);
  }, []);

  const cancelCombinedItemsButton = (
    <button
      onClick={() => {
        setCombinedDeliveries([]);
        setFetchCombinedItems(false);
      }}
      className="btn btn-warning btn-small"
    >
      <FormattedMessage id={'packer.routes.route.view.delivery.badges.cta.cancel.fetch'} />
    </button>
  );

  return (
    <div className="packer m-b-6">
      <Loader show={loading || editLoading || isCombinedOrderItemsLoading} />
      {isError || isEditError || isCombinedOrderItemsError ? (
        <ErrorMessage error={utils.getErrorMessageFromResponse(error || editError || combinedOrderItemsError)} />
      ) : (
        ''
      )}

      <div className="m-b-6">
        <Link to={routeLink} className="btn btn-normal-link">
          <FontAwesomeIcon icon={faArrowLeft} className="m-r-1" />
          <FormattedMessage id={'packer.routes.route.view.back.to.routes'} />
        </Link>
      </div>

      {route ? (
        <>
          <Helmet>
            <title>
              {route.driver
                ? formatMessage(
                    { id: 'packer.routes.route.view.title.with.driver' },
                    { driver: route.driver.name, routeId: route.id }
                  )
                : formatMessage({ id: 'packer.routes.route.view.title.no.driver' }, { routeId: route.id })}
            </title>
          </Helmet>
          <h2 className="m-b-3">
            {status ? `${status.packerIcon} ` : ''}
            {route.driver ? (
              <FormattedMessage
                id={'packer.routes.route.view.title.with.driver'}
                values={{ driver: route.driver.name, routeId: route.id }}
              />
            ) : (
              <FormattedMessage id={'packer.routes.route.view.title.no.driver'} values={{ routeId: route.id }} />
            )}
          </h2>
          {route.packer ? (
            <div>
              <FormattedMessage id={'packer.routes.route.view.packer'} values={{ packer: route.packer.screenName }} />
            </div>
          ) : (
            ''
          )}
          <div className="m-b-2">
            <FormattedMessage
              id={'packer.routes.route.view.status'}
              values={{ status: status ? `${status.name}` : route.status }}
            />
          </div>

          {route.status !== ROUTE_STATUS.waitingForPickup.name ? (
            <>
              <div className="m-b-3">
                <div className="m-b-2">
                  {route.deliveries.map((delivery) => {
                    const deliveryIndex = combinedDeliveries.findIndex((d) => d.id === delivery.id);

                    const badgeClasses = classNames({
                      'badge m-r-2 p-a-2 m-b-2': true,
                      'badge-secondary': deliveryIndex === -1,
                      'badge-primary': deliveryIndex !== -1,
                    });

                    return (
                      <span
                        key={delivery.id}
                        className={badgeClasses}
                        onClick={() => {
                          const newCombinedDeliveries = combinedDeliveries.slice();
                          if (deliveryIndex >= 0) {
                            newCombinedDeliveries.splice(deliveryIndex, 1);
                          } else {
                            newCombinedDeliveries.push(delivery);
                          }

                          setCombinedDeliveries(newCombinedDeliveries);
                        }}
                      >
                        {delivery.name}
                      </span>
                    );
                  })}
                </div>
                {combinedDeliveries.length || fetchCombinedItems ? (
                  <div>
                    {fetchCombinedItems ? (
                      cancelCombinedItemsButton
                    ) : (
                      <button onClick={() => setFetchCombinedItems(true)} className="btn btn-primary btn-small">
                        <FormattedMessage id={'packer.routes.route.view.delivery.badges.cta.fetch'} />
                      </button>
                    )}
                  </div>
                ) : (
                  ''
                )}
              </div>

              {fetchCombinedItems ? (
                <div className="row">
                  {prePackedCombinedDeliveryOrders?.length ? (
                    <div className="col-12 col-md-10 m-b-3">
                      <h3 className="m-b-3">
                        <FormattedMessage id={'packer.routes.route.view.combined.pre.packed.orders'} />
                      </h3>
                      {prePackedCombinedDeliveryOrders.map((order) => (
                        <OrderPackingStatusDisplay order={order} />
                      ))}
                    </div>
                  ) : (
                    ''
                  )}
                  <div className="col-12 col-md-10">
                    <div className="m-b-3">
                      <h3 className="m-b-3">
                        <FormattedMessage id={'packer.routes.route.view.combined.orders'} />
                      </h3>
                      {notPackedGroupedCombinedOrderItems
                        ? notPackedGroupedCombinedOrderItems.map((key) => {
                            return (
                              <div key={key} className="m-b-6">
                                <h4>{key}</h4>

                                <PackerCombinedDeliveryOrderItemsTable
                                  setPackedItemIds={setPackedCombinedItemIds}
                                  packedItemIds={packedCombinedItemIds}
                                  combinedOrderItems={groupedCombinedOrderItems[key]}
                                />
                              </div>
                            );
                          })
                        : ''}
                      {packedGroupedCombinedOrderItemKeys
                        ? packedGroupedCombinedOrderItemKeys.map((key) => {
                            return (
                              <div key={key} className="m-b-6">
                                <h4 className="text-muted text-strikethrough">{key}</h4>

                                <PackerCombinedDeliveryOrderItemsTable
                                  setPackedItemIds={setPackedCombinedItemIds}
                                  packedItemIds={packedCombinedItemIds}
                                  combinedOrderItems={groupedCombinedOrderItems[key]}
                                />
                              </div>
                            );
                          })
                        : ''}
                    </div>

                    {fetchCombinedItems ? cancelCombinedItemsButton : ''}
                  </div>
                </div>
              ) : (
                route.deliveries.map((delivery) => <PackerDeliveryRow key={delivery.id} delivery={delivery} />)
              )}
            </>
          ) : (
            ''
          )}

          <hr className="m-t-6 m-b-6" />

          <div className="m-b-6">
            {route.status === ROUTE_STATUS.waitingForPickup.name ? (
              <button
                className="btn btn-primary"
                onClick={() => editRoute({ route: { ...route, status: 'IN_PACKING' } })}
              >
                <FormattedMessage id={`packer.routes.route.view.cta.start.packing`} />
              </button>
            ) : isReady ? (
              <button
                className="btn btn-default"
                onClick={() => editRoute({ route: { ...route, status: 'WAITING_FOR_PICKUP' } })}
              >
                <FormattedMessage id={`packer.routes.route.view.cta.pending`} />
              </button>
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => {
                  editRoute({ route: { ...route, status: 'READY_FOR_PICKUP' } });
                }}
              >
                <FormattedMessage id={`packer.routes.route.view.cta.done`} />
              </button>
            )}
          </div>
        </>
      ) : (
        ''
      )}

      <div className="m-b-6">
        <Link to={routeLink} className="btn btn-normal-link">
          <FontAwesomeIcon icon={faArrowLeft} className="m-r-1" />
          <FormattedMessage id={'packer.routes.route.view.back.to.routes'} />
        </Link>
      </div>
    </div>
  );
};

PackerRouteView.propTypes = {};

PackerRouteView.defaultProps = {};

export default PackerRouteView;
